/**
 * SG PreSet plug-in — chatflows, panels (main.ts 에서 import).
 *
 *   import '@/features/startTask/preset_task/sg/preset';
 */

import './chatflows';
import './register';

import { registerPanel } from '../../shared/preset/panels/panelRegistry';
import { registerEFilingComponent } from '../../shared/preset/efilings/efilingRegistry';
import { AppointDirectorEFiling, ChangeAddressEFiling, IncorporationEFiling } from '../components';
import ControllerAppointmentEFiling from '../../../components/ControllerAppointmentEFiling.vue';
import ChangeFyeEFiling from '../../../components/ChangeFyeEFiling.vue';
import OfficerCessationEFiling from '../../../components/OfficerCessationEFiling.vue';
import SharesAllotmentEFiling from '../../../components/SharesAllotmentEFiling.vue';
import SharesTransferEFiling from '../../../components/SharesTransferEFiling.vue';
// B32111 — Appoint & Withdraw Director 통합 e-Filing (BM03B, SG 데몬 경유)
import AppointWithdrawOfficerEFiling from './appoint-withdraw-officer/AppointWithdrawOfficerEFiling.vue';
// B32103 — Appoint Secretary e-Filing (BM03B · positionHeld LC_SEC, SG 데몬 경유)
import AppointSecretaryOfficerEFiling from './appoint-secretary-officer/AppointSecretaryOfficerEFiling.vue';
import type { PreSetSelection } from '../../shared/preset/types/preset.types';
import PreSetAppointDirectorPanel from './components/PreSetAppointDirectorPanel.vue';
import PreSetAppointSecretaryPanel from './components/PreSetAppointSecretaryPanel.vue';
import PreSetChangeFinancialYearEndPanel from './components/PreSetChangeFinancialYearEndPanel.vue';
import PreSetChangeAddressPanel from './components/PreSetChangeAddressPanel.vue';
import PreSetWithdrawDirectorPanel from './components/PreSetWithdrawDirectorPanel.vue';
import PreSetAppointWithdrawDirectorPanel from './components/PreSetAppointWithdrawDirectorPanel.vue';
import PreSetWithdrawSecretaryPanel from './components/PreSetWithdrawSecretaryPanel.vue';
import PreSetAgmUfsPanel from './components/PreSetAgmUfsPanel.vue';
import PreSetAgmAfsPanel from './components/PreSetAgmAfsPanel.vue';
import PreSetAgmExemptedAfsPanel from './components/PreSetAgmExemptedAfsPanel.vue';
import PreSetAgmExemptedUfsPanel from './components/PreSetAgmExemptedUfsPanel.vue';
import PreSetAllotmentSharesPanel from './components/PreSetAllotmentSharesPanel.vue';
import PreSetTransferSharesPanel from './components/PreSetTransferSharesPanel.vue';
import PreSetShareCertificatePanel from './components/PreSetShareCertificatePanel.vue';
import PreSetNoticeIndividualControllerPanel from './components/PreSetNoticeIndividualControllerPanel.vue';
import PreSetNoticeCorporateControllerPanel from './components/PreSetNoticeCorporateControllerPanel.vue';
import PreSetConfirmationControllersPanel from './components/PreSetConfirmationControllersPanel.vue';
import PreSetIncorporationPrePanel from './components/PreSetIncorporationPrePanel.vue';
import PreSetIncorporationPostPanel from './components/PreSetIncorporationPostPanel.vue';
import PreSetFinalDividendsPanel from './components/PreSetFinalDividendsPanel.vue';
import PreSetInterimDividendsPanel from './components/PreSetInterimDividendsPanel.vue';
import PreSetAppointWithdrawCeoPanel from './components/PreSetAppointWithdrawCeoPanel.vue';
import PreSetAppointWithdrawMdPanel from './components/PreSetAppointWithdrawMdPanel.vue';
import PreSetAppointDpoPanel from './components/PreSetAppointDpoPanel.vue';
import { SG_AGM_AFS_TASK_CODE } from '../constants/sgAgmTaskCodes';

function isSgSelection(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code.startsWith('S')) return true;
  if (sel.category.COUNTRY_CD === 'SG') return true;
  if (sel.parentCategory?.COUNTRY_CD === 'SG') return true;
  return false;
}

function matchesChangeFye(sel: PreSetSelection): boolean {
  const text = `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return /financial\s*year|fye|year\s*end/.test(text);
}

function matchesWithdrawDirector(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32102') return true;                          // 해임 전용 코드
  if (code === 'B32111' || code === 'B32101') return false;    // 통합/선임은 절대 아님
  // 텍스트 폴백은 **task 이름만** (공유 카테고리명 "Appoint/Withdraw Director" 가
  //   appoint-only 태스크까지 가로채던 문제 방지 — category/parent 제외).
  const text = String(sel.task.TASK_NAME || '').toLowerCase();
  return /withdraw\s*director|director\s*resignation|resignation\s*of\s*director/.test(text);
}

/** 이사 선임+해임 동시 (B32111) — Form 45(선임 N) + Resignation Letter(사임 M) + DR 1부. */
function matchesAppointWithdrawDirector(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  // 선임+해임 통합은 **오직 B32111**. (Appoint Director=B32101, Withdraw Director=B32102 은 별개 프리셋)
  if (code === 'B32111') return isSgSelection(sel);
  // 다른 선임/해임 코드(B32101/B32102 등)면 절대 가로채지 않는다.
  if (/^B321(01|02)$/.test(code) || /^B320(01|04)$/.test(code)) return false;
  // 텍스트 폴백은 **task 이름만** 본다(공유 카테고리명 "Appoint/Withdraw Director" 가
  //   appoint-only/withdraw-only 태스크까지 가로채던 버그 방지 — category/parent 제외).
  const text = String(sel.task.TASK_NAME || '').toLowerCase();
  return isSgSelection(sel) && /appoint.*withdraw.*director|withdraw.*appoint.*director/.test(text);
}

function matchesWithdrawSecretary(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32104') return isSgSelection(sel);
  if (code === 'B32103') return false; // 비서 선임은 절대 아님
  // 텍스트 폴백은 **task 이름만** (공유 카테고리명이 선임 태스크를 가로채는 것 방지).
  const text = String(sel.task.TASK_NAME || '').toLowerCase();
  return (
    isSgSelection(sel) &&
    /withdraw\s*secretary|secretary\s*withdrawal|resignation\s*of\s*secretary|secretary\s*resignation|cessation\s*of\s*secretary|company\s*secretary\s*cessation/.test(
      text,
    )
  );
}

function matchesChangeAddress(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32105') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return /change\s*(of\s*)?registered\s*office\s*address|change\s*(of\s*)?address|office\s*address/.test(text);
}

function matchesAppointDirector(sel: PreSetSelection): boolean {
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return /appoint\s*director|appointment\s*of\s*director|director\s*appointment|new\s*director/.test(text);
}

function matchesAgmAfs(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === SG_AGM_AFS_TASK_CODE) return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return (
    isSgSelection(sel)
    && /agm/.test(text)
    && /audited/.test(text)
    && !/exempted/.test(text)
    && !/unaudited/.test(text)
  );
}

function matchesAgmUfs(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32201') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  if (/exempted/.test(text)) return false;
  if (matchesAgmAfs(sel)) return false;
  return /agm\s*(with\s*)?unaudited|unaudited\s*financial\s*statements/.test(text);
}

function matchesAgmExemptedUfs(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32203') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return (
    isSgSelection(sel)
    && /exempted/.test(text)
    && /unaudited/.test(text)
  );
}

function matchesAgmExemptedAfs(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32202') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return (
    isSgSelection(sel)
    && /exempted/.test(text)
    && /audited/.test(text)
    && !/unaudited/.test(text)
  );
}

function matchesAppointSecretary(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32103') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return (
    isSgSelection(sel) &&
    /appoint\s*secretary|appointment\s*of\s*secretary|appointment\s*of\s*company\s*secretary|secretary\s*appointment|new\s*secretary/.test(
      text,
    )
  );
}

function matchesAllotmentShares(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32005') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /allotment\s*of\s*shares|share\s*allotment|allot\s*shares/.test(text);
}

function matchesTransferShares(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32006') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /transfer\s*of\s*shares|share\s*transfer|transfer\s*shares/.test(text);
}

function matchesShareCertificate(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32013') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /share\s*certificate/.test(text);
}

function matchesNoticeCorporateController(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32107' || code === 'B32007') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  const docText = (sel.documents || [])
    .map((d) => `${d.templateName || ''} ${d.docCode || ''}`.toLowerCase())
    .join(' ');
  const combined = `${text} ${docText}`;
  return (
    isSgSelection(sel) &&
    (/386ag.*\(.*corporate|notice.*controller.*corporate|corporate.*controller.*notice|controller.*notice.*corporate/.test(
      combined,
    ) ||
      /notice for controllers?.*corporate/.test(combined))
  );
}

function matchesConfirmationControllers(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32108') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  const docText = (sel.documents || [])
    .map((d) => `${d.templateName || ''} ${d.docCode || ''}`.toLowerCase())
    .join(' ');
  const combined = `${text} ${docText}`;
  return (
    isSgSelection(sel) &&
    (/confirmation\s+of\s+registrable\s+controllers?|confirm(?:ation)?\s+registrable\s+controllers?|registrable\s+controller\s+confirmation/.test(
      combined,
    ))
  );
}

function matchesNoticeIndividualController(sel: PreSetSelection): boolean {
  if (matchesNoticeCorporateController(sel)) return false;
  if (matchesConfirmationControllers(sel)) return false;
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32106') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  const docText = (sel.documents || [])
    .map((d) => `${d.templateName || ''} ${d.docCode || ''}`.toLowerCase())
    .join(' ');
  const combined = `${text} ${docText}`;
  return (
    isSgSelection(sel) &&
    (/386ag.*\(.*individual|notice.*controller.*individual|individual.*controller.*notice|controller.*notice.*individual/.test(
      combined,
    ) ||
      /notice for controllers?.*individual/.test(combined))
  );
}

function matchesFinalDividends(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32112') return isSgSelection(sel);
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /final\s*dividend/.test(text);
}

function matchesInterimDividends(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32113') return isSgSelection(sel);
  if (code === 'B32112') return false; // Final Dividends
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /interim\s*dividend/.test(text);
}

/** Appoint / Withdraw CEO (B32114) — DR 1부(CEO 선임 + 사임). 이름 매칭은 CEO/Chief Executive 가 있을 때만. */
function matchesAppointWithdrawCeo(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32114') return isSgSelection(sel);
  if (/^B32\d{3}$/.test(code)) return false; // 다른 SG 프리셋 코드는 가로채지 않음
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /\bceo\b|chief\s*executive/.test(text);
}

/** Appoint / Withdraw MD (B32115) — DR 1부(MD 선임 + 사임). 이름 매칭은 Managing Director 가 있을 때만. */
function matchesAppointWithdrawMd(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32115') return isSgSelection(sel);
  if (/^B32\d{3}$/.test(code)) return false; // 다른 SG 프리셋 코드는 가로채지 않음
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /managing\s*director|\bmd\b/.test(text);
}

/** Appointment of DPO (B32116) — 결의 + 위임장 1부. 이름 매칭은 DPO / Data Protection Officer 가 있을 때만. */
function matchesAppointDpo(sel: PreSetSelection): boolean {
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32116') return isSgSelection(sel);
  if (/^B32\d{3}$/.test(code)) return false; // 다른 SG 프리셋 코드는 가로채지 않음
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return isSgSelection(sel) && /\bdpo\b|data\s*protection\s*officer/.test(text);
}

function matchesIncorporationPost(sel: PreSetSelection): boolean {
  if (!isSgSelection(sel)) return false;
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32110') return true;
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  return /incorporation\s*\(?\s*post\s*\)?|post[\s-]*incorporation/.test(text);
}

function matchesIncorporationPre(sel: PreSetSelection): boolean {
  if (!isSgSelection(sel)) return false;
  if (matchesIncorporationPost(sel)) return false;
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  if (code === 'B32109') return true;
  const text =
    `${sel.parentCategory?.CATEGORY_NAME || ''} ${sel.category.CATEGORY_NAME} ${sel.task.TASK_NAME}`.toLowerCase();
  // Incorporation (Pre) — Post 와 구분
  if (/incorporation\s*\(?\s*post\s*\)?|post[\s-]*incorporation/.test(text)) return false;
  return /incorporation\s*\(?\s*pre\s*\)?|incorporation\s*pre/.test(text);
}

registerPanel({
  id: 'sg-incorporation-post',
  priority: 41,
  match: (sel) => matchesIncorporationPost(sel),
  component: PreSetIncorporationPostPanel,
  meta: { docType: 'SG_INCORPORATION_POST', studio: true },
});

registerPanel({
  id: 'sg-incorporation-pre',
  priority: 40,
  match: (sel) => matchesIncorporationPre(sel),
  component: PreSetIncorporationPrePanel,
  // studioPageScroll — 문서 칸 대신 본문 전체 스크롤(스크롤바 오른쪽 끝). 우선 Incorporation Pre 만.
  meta: { docType: 'SG_INCORPORATION_PRE', studio: true, studioPageScroll: true },
});

registerPanel({
  id: 'sg-final-dividends',
  priority: 29,
  match: (sel) => matchesFinalDividends(sel),
  component: PreSetFinalDividendsPanel,
  meta: { docType: 'SG_FINAL_DIVIDENDS', studio: true },
});

registerPanel({
  id: 'sg-interim-dividends',
  priority: 29,
  match: (sel) => matchesInterimDividends(sel),
  component: PreSetInterimDividendsPanel,
  meta: { docType: 'SG_INTERIM_DIVIDENDS', studio: true },
});

// Appoint / Withdraw CEO (B32114) — 임원 계열(director 20~30)보다 우선.
registerPanel({
  id: 'sg-appoint-withdraw-ceo',
  priority: 31,
  match: (sel) => matchesAppointWithdrawCeo(sel),
  component: PreSetAppointWithdrawCeoPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION', studio: true },
});

// Appoint / Withdraw MD (B32115) — 'director' 계열 패널보다 우선.
registerPanel({
  id: 'sg-appoint-withdraw-md',
  priority: 31,
  match: (sel) => matchesAppointWithdrawMd(sel),
  component: PreSetAppointWithdrawMdPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION', studio: true },
});

// Appointment of DPO (B32116) — 'director' 계열 패널보다 우선.
registerPanel({
  id: 'sg-appoint-dpo',
  priority: 31,
  match: (sel) => matchesAppointDpo(sel),
  component: PreSetAppointDpoPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION', studio: true },
});

registerPanel({
  id: 'sg-agm-afs',
  priority: 28,
  match: (sel) => matchesAgmAfs(sel),
  component: PreSetAgmAfsPanel,
  meta: { docType: 'SG_AGM_AFS', studio: true },
});

registerPanel({
  id: 'sg-agm-exempted-ufs',
  priority: 27,
  match: (sel) => matchesAgmExemptedUfs(sel),
  component: PreSetAgmExemptedUfsPanel,
  meta: { docType: 'SG_AGM_EXEMPTED_UFS', studio: true },
});

registerPanel({
  id: 'sg-agm-exempted-afs',
  priority: 26,
  match: (sel) => matchesAgmExemptedAfs(sel),
  component: PreSetAgmExemptedAfsPanel,
  meta: { docType: 'SG_AGM_EXEMPTED_AFS', studio: true },
});

registerPanel({
  id: 'sg-agm-ufs',
  priority: 25,
  match: (sel) => isSgSelection(sel) && matchesAgmUfs(sel),
  component: PreSetAgmUfsPanel,
  meta: { docType: 'SG_AGM_UFS', studio: true },
});

registerPanel({
  id: 'sg-allotment-shares',
  priority: 24,
  match: (sel) => matchesAllotmentShares(sel),
  component: PreSetAllotmentSharesPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION' },
});

registerPanel({
  id: 'sg-transfer-shares',
  priority: 23,
  match: (sel) => matchesTransferShares(sel),
  component: PreSetTransferSharesPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION' },
});

registerPanel({
  id: 'sg-share-certificate',
  priority: 23,
  match: (sel) => matchesShareCertificate(sel),
  component: PreSetShareCertificatePanel,
  meta: { docType: 'SG_SHARE_CERTIFICATE', studio: true },
});

registerPanel({
  id: 'sg-controller-confirmation',
  priority: 24,
  match: (sel) => matchesConfirmationControllers(sel),
  component: PreSetConfirmationControllersPanel,
  meta: { docType: 'SG_CONTROLLER_CONFIRMATION' },
});

registerPanel({
  id: 'sg-controller-notice-corporate',
  priority: 23,
  match: (sel) => matchesNoticeCorporateController(sel),
  component: PreSetNoticeCorporateControllerPanel,
  meta: { docType: 'SG_CONTROLLER_NOTICE_CORPORATE' },
});

registerPanel({
  id: 'sg-controller-notice-individual',
  priority: 22,
  match: (sel) => matchesNoticeIndividualController(sel),
  component: PreSetNoticeIndividualControllerPanel,
  meta: { docType: 'SG_CONTROLLER_NOTICE_INDIVIDUAL' },
});

registerPanel({
  id: 'sg-appoint-secretary',
  priority: 22,
  match: (sel) => matchesAppointSecretary(sel),
  component: PreSetAppointSecretaryPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION', studio: true },
});

registerPanel({
  id: 'sg-appoint-director',
  priority: 20,
  match: (sel) => isSgSelection(sel) && matchesAppointDirector(sel),
  component: PreSetAppointDirectorPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION', studio: true },
});

registerPanel({
  id: 'sg-change-financial-year-end',
  priority: 20,
  match: (sel) => isSgSelection(sel) && matchesChangeFye(sel),
  component: PreSetChangeFinancialYearEndPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION' },
});

registerPanel({
  id: 'sg-change-address',
  priority: 20,
  match: (sel) => isSgSelection(sel) && matchesChangeAddress(sel),
  component: PreSetChangeAddressPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION', studio: true },
});

registerPanel({
  id: 'sg-withdraw-secretary',
  priority: 21,
  match: (sel) => matchesWithdrawSecretary(sel),
  component: PreSetWithdrawSecretaryPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION' },
});

registerPanel({
  id: 'sg-withdraw-director',
  priority: 20,
  match: (sel) => isSgSelection(sel) && matchesWithdrawDirector(sel),
  component: PreSetWithdrawDirectorPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION' },
});
// 선임+해임 동시(B32111) — priority 30 으로 appoint/withdraw 단일 패널(20)보다 우선.
registerPanel({
  id: 'sg-appoint-withdraw-director',
  priority: 30,
  match: (sel) => matchesAppointWithdrawDirector(sel),
  component: PreSetAppointWithdrawDirectorPanel,
  meta: { docType: 'SG_DIRECTORS_RESOLUTION', studio: true },
});

registerEFilingComponent('B32001', AppointDirectorEFiling);
registerEFilingComponent('B32101', AppointDirectorEFiling);
registerEFilingComponent('B32103', AppointSecretaryOfficerEFiling); // Appoint Secretary (BM03B · LC_SEC, SG 데몬 경유)
registerEFilingComponent('B32009', ChangeFyeEFiling);
registerEFilingComponent('B32105', ChangeAddressEFiling);
registerEFilingComponent('B32005', SharesAllotmentEFiling);
registerEFilingComponent('B32006', SharesTransferEFiling);
registerEFilingComponent('B32004', OfficerCessationEFiling);
registerEFilingComponent('B32102', OfficerCessationEFiling);
registerEFilingComponent('B32104', OfficerCessationEFiling); // Secretary withdrawal — same shell as director cessation
registerEFilingComponent('B32106', ControllerAppointmentEFiling); // Notice for Controller (Individual)
registerEFilingComponent('B32107', ControllerAppointmentEFiling); // Notice for Controller (Corporate)
registerEFilingComponent('B32007', ControllerAppointmentEFiling); // Notice for Controller (Corporate) / appointment shell
registerEFilingComponent('B32109', IncorporationEFiling); // SG Incorporation (Pre) — review before bizfile submission
registerEFilingComponent('B32111', AppointWithdrawOfficerEFiling); // Appoint & Withdraw Director 통합 (BM03B, SG 데몬 경유)
registerEFilingComponent('B32114', AppointWithdrawOfficerEFiling); // Appoint / Withdraw CEO (BM03B · LC_CEO)
registerEFilingComponent('B32115', AppointWithdrawOfficerEFiling); // Appoint / Withdraw MD (BM03B · LC_MD)
